import { formatValueType } from "../../utils/valueType";

const formatValue = (value?: ValueType | Value): string => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "object" && "value" in value) {
    return formatValueType(value.value);
  }

  return formatValueType(value as ValueType);
};

const getItemTypeKey = (item: ArenaOnlyItem | Item): string => {
  if ("item_type" in item && item.item_type?.uuid) {
    return item.item_type.uuid;
  }

  return item.item_type_name || "unknown";
};

const getProcessTypeKey = (process: ProcessWithSteps): string => {
  return process.process.process_type.uuid || process.process.process_type.name;
};

const getChildSummary = (child: BOMItem): string => {
  return `${child.item.name} (${child.item.arena_part_number}) x${child.quantity}`;
};

export type DiffRow = {
  label: string;
  oldValue: string;
  newValue: string;
};

export type DiffSection = {
  rows: DiffRow[];
  changeCount: number;
};

const getTemplateMetadata = (item?: ArenaOnlyItem | Item | null) => {
  if (!item) {
    return { name: "", description: "" };
  }

  if ("base_template" in item && item.base_template) {
    return {
      name: item.base_template.name || "",
      description: item.base_template.description || "",
    };
  }

  return { name: "", description: "" };
};

const getItemDescription = (item?: ArenaOnlyItem | Item | null): string => {
  if (!item) {
    return "";
  }

  if ("description" in item) {
    return item.description || "";
  }

  return "";
};

export const diffMetadata = (
  oldItem: ItemWithBOM | null,
  newItem: ItemWithBOM
): DiffSection => {
  const oldItemData = oldItem?.item || null;
  const newItemData = newItem.item;
  const oldTemplate = getTemplateMetadata(oldItemData);
  const newTemplate = getTemplateMetadata(newItemData);

  const fields: Array<{ label: string; oldValue: string; newValue: string }> =
    [
      {
        label: "Part Number",
        oldValue: oldItemData?.arena_part_number || "",
        newValue: newItemData?.arena_part_number || "",
      },
      {
        label: "Template Name",
        oldValue: oldTemplate.name,
        newValue: newTemplate.name,
      },
      {
        label: "Template Description",
        oldValue: oldTemplate.description,
        newValue: newTemplate.description,
      },
      {
        label: "Item Name",
        oldValue: oldItemData?.name || "",
        newValue: newItemData?.name || "",
      },
      {
        label: "Item Description",
        oldValue: getItemDescription(oldItemData),
        newValue: getItemDescription(newItemData),
      },
    ];

  const rows = fields.filter((field) => field.oldValue !== field.newValue);
  return { rows, changeCount: rows.length };
};

export const diffAttributes = (
  oldItem: ItemWithBOM | null,
  newItem: ItemWithBOM
): DiffSection => {
  const oldAttributes = oldItem?.item.attributes || {};
  const newAttributes = newItem.item.attributes || {};
  const keys = new Set([
    ...Object.keys(oldAttributes),
    ...Object.keys(newAttributes),
  ]);

  const rows: DiffRow[] = [];

  Array.from(keys)
    .sort()
    .forEach((key) => {
      const oldValue = formatValue(oldAttributes[key]);
      const newValue = formatValue(newAttributes[key]);
      if (oldValue !== newValue) {
        rows.push({ label: key, oldValue, newValue });
      }
    });

  return { rows, changeCount: rows.length };
};

export const diffProcesses = (
  oldItem: ItemWithBOM | null,
  newItem: ItemWithBOM
): DiffSection => {
  const oldProcesses = oldItem?.processes || [];
  const newProcesses = newItem.processes || [];
  const oldByType = new Map<string, ProcessWithSteps[]>();

  oldProcesses.forEach((process) => {
    const key = getProcessTypeKey(process);
    const list = oldByType.get(key) || [];
    list.push(process);
    oldByType.set(key, list);
  });

  const usedByType = new Map<string, number>();
  const rows: DiffRow[] = [];

  newProcesses.forEach((process) => {
    const key = getProcessTypeKey(process);
    const oldList = oldByType.get(key) || [];
    const used = usedByType.get(key) || 0;
    const oldMatch = oldList[used] || null;
    usedByType.set(key, used + 1);

    const processLabel = process.process.process_type.name;

    if (!oldMatch) {
      rows.push({
        label: processLabel,
        oldValue: "",
        newValue: process.process.name || processLabel,
      });
      return;
    }

    if (oldMatch.process.name !== process.process.name) {
      rows.push({
        label: `${processLabel} / Name`,
        oldValue: oldMatch.process.name,
        newValue: process.process.name,
      });
    }

    const oldParams = oldMatch.process.parameters || {};
    const newParams = process.process.parameters || {};
    const paramKeys = new Set([
      ...Object.keys(oldParams),
      ...Object.keys(newParams),
    ]);

    Array.from(paramKeys)
      .sort()
      .forEach((paramKey) => {
        const oldValue = formatValue(oldParams[paramKey]);
        const newValue = formatValue(newParams[paramKey]);
        if (oldValue !== newValue) {
          rows.push({
            label: `${processLabel} / ${paramKey}`,
            oldValue,
            newValue,
          });
        }
      });
  });

  oldByType.forEach((oldList, key) => {
    const used = usedByType.get(key) || 0;
    const remaining = oldList.slice(used);
    remaining.forEach((oldProcess) => {
      rows.push({
        label: oldProcess.process.process_type.name,
        oldValue: oldProcess.process.name || oldProcess.process.process_type.name,
        newValue: "",
      });
    });
  });

  return { rows, changeCount: rows.length };
};

export const diffChildren = (
  oldItem: ItemWithBOM | null,
  newItem: ItemWithBOM
): DiffSection => {
  const oldChildren = oldItem?.bom || [];
  const newChildren = newItem.bom || [];
  const oldByType = new Map<string, BOMItem[]>();

  oldChildren.forEach((child) => {
    const key = getItemTypeKey(child.item);
    const list = oldByType.get(key) || [];
    list.push(child);
    oldByType.set(key, list);
  });

  const usedByType = new Map<string, number>();
  const rows: DiffRow[] = [];

  newChildren.forEach((child) => {
    const key = getItemTypeKey(child.item);
    const oldList = oldByType.get(key) || [];
    const used = usedByType.get(key) || 0;
    const oldMatch = oldList[used] || null;
    usedByType.set(key, used + 1);

    const label = child.item.item_type_name;

    if (!oldMatch) {
      rows.push({
        label,
        oldValue: "",
        newValue: getChildSummary(child),
      });
      return;
    }

    const oldSummary = getChildSummary(oldMatch);
    const newSummary = getChildSummary(child);
    if (oldSummary !== newSummary) {
      rows.push({ label, oldValue: oldSummary, newValue: newSummary });
    }
  });

  oldByType.forEach((oldList, key) => {
    const used = usedByType.get(key) || 0;
    const remaining = oldList.slice(used);
    remaining.forEach((child) => {
      rows.push({
        label: child.item.item_type_name,
        oldValue: getChildSummary(child),
        newValue: "",
      });
    });
  });

  return { rows, changeCount: rows.length };
};

export const getItemChangeCount = (
  oldItem: ItemWithBOM | null,
  newItem: ItemWithBOM
): number => {
  const metadataCount = diffMetadata(oldItem, newItem).changeCount;
  const attributeCount = diffAttributes(oldItem, newItem).changeCount;
  const processCount = diffProcesses(oldItem, newItem).changeCount;
  const childrenCount = diffChildren(oldItem, newItem).changeCount;

  return metadataCount + attributeCount + processCount + childrenCount;
};

export type DiffNode = {
  id: string;
  oldItem: ItemWithBOM | null;
  newItem: ItemWithBOM;
  children: DiffNode[];
};

export const buildDiffTree = (
  oldItem: ItemWithBOM | null,
  newItem: ItemWithBOM,
  path: string = ""
): DiffNode => {
  const oldChildren = oldItem?.bom || [];
  const newChildren = newItem.bom || [];
  const oldByType = new Map<string, BOMItem[]>();

  oldChildren.forEach((child) => {
    const key = getItemTypeKey(child.item);
    const list = oldByType.get(key) || [];
    list.push(child);
    oldByType.set(key, list);
  });

  const usedByType = new Map<string, number>();
  const children = newChildren.map((child, index) => {
    const key = getItemTypeKey(child.item);
    const oldList = oldByType.get(key) || [];
    const used = usedByType.get(key) || 0;
    const oldMatch = oldList[used] || null;
    usedByType.set(key, used + 1);

    const childPath = path ? `${path}>${index}` : `${index}`;
    return buildDiffTree(oldMatch, child, childPath);
  });

  return {
    id: path,
    oldItem,
    newItem,
    children,
  };
};

export const findDiffNodeByPath = (
  root: DiffNode | null,
  path: string
): DiffNode | null => {
  if (!root) return null;
  if (root.id === path) return root;

  for (const child of root.children) {
    const found = findDiffNodeByPath(child, path);
    if (found) return found;
  }

  return null;
};

export const collectDiffPaths = (
  node: DiffNode | null,
  paths: Set<string> = new Set()
): Set<string> => {
  if (!node) return paths;

  const changeCount = getItemChangeCount(node.oldItem, node.newItem);
  if (changeCount > 0) {
    paths.add(node.id);
  }

  node.children.forEach((child) => collectDiffPaths(child, paths));
  return paths;
};
